import { supabase } from '../utilities/supabase';
import { resolveRelativeDateRange } from '../utilities/resolveRelativeDateRange';

// 'YYYY-MM-DD' values come from the date pickers; any other range value is a number.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const hasBound = x => x !== null && x !== undefined && x !== '';
// The calendar day after a 'YYYY-MM-DD' string (pure date maths, no time zone).
const nextDay = ymd => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};
// Midnight at the start of a 'YYYY-MM-DD' day in UK time, with its UTC offset,
// e.g. '2026-05-10T00:00:00+01:00' (BST) or '2026-01-10T00:00:00+00:00' (GMT).
// Staff work to UK days whatever their computer's time zone. UK clocks change
// at 01:00 UTC, so the offset at 00:00 UTC of a date is the offset at UK
// midnight of that date (also on both change days). Postgres reads the result
// as UK midnight for timestamptz columns and as the same calendar day for DATE
// columns.
const ukHourFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  hourCycle: 'h23'
});
const ukMidnight = ymd => {
  const [y, m, d] = ymd.split('-').map(Number);
  const bst = Number(ukHourFormat.format(new Date(Date.UTC(y, m - 1, d)))) === 1;
  return `${ymd}T00:00:00${bst ? '+01:00' : '+00:00'}`;
};
const dayStart = x => (DATE_ONLY.test(String(x)) ? ukMidnight(String(x)) : x);

/**
 * BaseModel class for handling common database operations.
 */
class BaseModel {
  /**
   * Create a new BaseModel instance.
   * @param {Object} data - The data to initialize the model with.
   */
  constructor(data = {}) {
    this.id = data.id || null;
    this.displayId = data.display_id || null;
    this.createdDate = data.created_date || new Date();
    this.updatedDate = data.updated_date || new Date();
  }

  /** The table name in the database. Should be overridden by subclasses. */
  static table = '';

  /** The default select query. Can be overridden by subclasses for more specific queries. */
  static selectQuery = '*';

  /**
   * Convert the model instance to a database-friendly format.
   * @returns {Object} An object representation of the model for database operations.
   */
  toDatabase() {
    return {
      id: this.id,
      created_date: this.createdDate || undefined,
      updated_date: new Date().toISOString()
    };
  }

  /**
   * Remember the row as loaded, so update() only sends the fields that changed
   * since. A full-row write would put back values the database or someone else
   * changed after this record was loaded (e.g. a client's tags step).
   * Values are kept as JSON so in-place edits (array push) still count as changes.
   */
  rememberSaved() {
    try {
      const saved = {};
      Object.entries(this.toDatabase()).forEach(([key, value]) => {
        saved[key] = JSON.stringify(value);
      });
      Object.defineProperty(this, '_saved', {
        value: saved,
        writable: true,
        configurable: true,
        enumerable: false
      });
    } catch {
      // No snapshot: update() falls back to writing the whole row.
    }
  }

  /**
   * Fetch a single record from the database based on a field and value.
   * @param {string} field - The field to query on.
   * @param {*} value - The value to match.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  static async fetchOne(field, value, query, object = true) {
    try {
      const { data, error } = await supabase
        .from(this.table)
        .select(query || this.selectQuery)
        .eq(field, value)
        .single();

      if (error) throw error;

      return object ? new this(data) : data;
    } catch (error) {
      console.error(`[Error] Fetching ${this.name}: `, error);
      throw error;
    }
  }

  /**
   * Get a record by its ID.
   * @param {string|number} id - The ID of the record to fetch.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  static async getById(id) {
    return this.fetchOne('id', id);
  }

  /**
   * Insert the current instance into the database.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  async insert() {
    try {
      const { data, error } = await supabase
        .from(this.constructor.table)
        .insert(
          [this.toDatabase()].map(
            ({ id, created_date, updated_date, ...rest }) => rest
          )
        )
        .select(this.constructor.selectQuery);

      if (error) throw error;

      return new this.constructor(data[0]);
    } catch (error) {
      console.error(`[Error] Inserting ${this.constructor.name}: `, error);
      throw error;
    }
  }

  /**
   * Update the current instance in the database.
   * @param {Object} updatedFields - The fields to update.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  async update(updatedFields) {
    // The fields as they were, so a rejected save leaves this instance (often
    // the one on screen) showing what is stored, not the value that failed.
    const before = Object.keys(updatedFields || {}).map(key => [
      key,
      Object.getOwnPropertyDescriptor(this, key)
    ]);
    let stored = false;
    try {
      Object.assign(this, updatedFields);
      let payload = this.toDatabase();
      const saved = this._saved;
      if (saved) {
        payload = Object.fromEntries(
          Object.entries(payload).filter(
            ([key, value]) =>
              key === 'updated_date' || JSON.stringify(value) !== saved[key]
          )
        );
      }

      const { data, error } = await supabase
        .from(this.constructor.table)
        .update(payload)
        .eq('id', this.id)
        .select(this.constructor.selectQuery);

      if (error) throw error;
      stored = true;

      // Callers that keep using this instance save against what is now stored.
      if (saved) this.rememberSaved();

      if (data) {
        return new this.constructor(data[0]);
      }
    } catch (error) {
      if (!stored) {
        before.forEach(([key, descriptor]) => {
          if (descriptor) Object.defineProperty(this, key, descriptor);
          else delete this[key];
        });
      }
      console.error(`[Error] Updating ${this.constructor.name}: `, error);
      throw error;
    }
  }

  /**
   * Delete the current instance from the database.
   * @returns {Promise<void>}
   */
  async delete() {
    try {
      const { error } = await supabase
        .from(this.constructor.table)
        .delete()
        .eq('id', this.id);

      if (error) throw error;
    } catch (error) {
      console.error(`[Error] Deleting ${this.constructor.name}: `, error);
      throw error;
    }
  }

  /**
   * Apply filters to a query.
   * @param {Object} query - The Supabase query object.
   * @param {Object} filters - The filters to apply.
   * @returns {Object} The modified query object.
   */
  static filters(query, filters) {
    Object.entries(filters).forEach(([key, filter]) => {
      // Skip isAdvancedMode as it's only used for determining nullsLast behavior
      if (key === 'isAdvancedMode') return;
      if (filter) {
        const { operator, innerOperator, value, isOr, isArray, field: fieldOverride } = filter;
        // Allow filter to specify the actual column name (for duplicate key scenarios like __lte suffixes)
        const columnKey = fieldOverride || key;
        // Special handling for pension_values JSONB array
        if (key === 'stats->pension_values' && operator === 'cs') {
          const types = Array.isArray(value) ? value : [value];
          // Always use OR syntax for consistency, even with single value
          const orConditions = types
            .map(type => `stats->pension_values.cs.[{"type":"${type}"}]`)
            .join(',');
          query = query.or(orConditions);
        } else if (isOr && Array.isArray(value) && value.length > 1) {
          const orConditions = value
            .map(val =>
              isArray
                ? `${columnKey}.${operator}.{${val}}`
                : `${columnKey}.${operator}.(${val})`
            )
            .join(',');
          query = query.or(orConditions);
        } else {
          switch (operator) {
            case 'eq':
              query = query.eq(columnKey, value);
              break;
            case 'neq':
              query = query.neq(columnKey, value);
              break;
            case 'gt':
              query = query.gt(columnKey, value);
              break;
            case 'gte':
              query = query.gte(columnKey, value);
              break;
            case 'lt':
              query = query.lt(columnKey, value);
              break;
            case 'lte':
              query = query.lte(columnKey, value);
              break;
            case 'like':
              query = query.like(columnKey, value);
              break;
            case 'ilike':
              query = query.ilike(columnKey, `%${value}%`);
              break;
            case 'is':
              query = query.is(columnKey, value);
              break;
            case 'in':
              query = query.in(columnKey, value);
              break;
            case 'not.in':
              query = query.not(columnKey, 'in', value);
              break;
            case 'contains':
              query = query.contains(columnKey, value);
              break;
            case 'cs':
              query = query.contains(columnKey, value);
              break;
            case 'containedBy':
              query = query.containedBy(columnKey, value);
              break;
            case 'rangeGt':
              query = query.rangeGt(columnKey, value);
              break;
            case 'rangeGte':
              query = query.rangeGte(columnKey, value);
              break;
            case 'rangeLt':
              query = query.rangeLt(columnKey, value);
              break;
            case 'rangeLte':
              query = query.rangeLte(columnKey, value);
              break;
            case 'textSearch':
              if (value) {
                query = query.textSearch(
                  columnKey,
                  value.replaceAll(' ', '+').concat('', ':*')
                );
              }
              break;
            case 'match':
              query = query.match(columnKey, value);
              break;
            case 'not':
              query = query.not(columnKey, innerOperator, value);
              break;
            case 'or':
              query = query.or(value);
              break;
            case 'filter':
              query = query.filter(columnKey, operator, value);
              break;
            case 'between': {
              const raw = typeof value === 'string' ? JSON.parse(value) : value;
              const isRelative = raw?._relative === true;
              const v = resolveRelativeDateRange(raw) || {};
              let from = hasBound(v.from) ? v.from : null;
              let to = hasBound(v.to) ? v.to : null;
              // End/To typed lower than Start/From: use the range the right way round
              if (from !== null && to !== null) {
                const numeric = !isNaN(Number(from)) && !isNaN(Number(to));
                if (numeric ? Number(from) > Number(to) : String(from) > String(to)) {
                  [from, to] = [to, from];
                }
              }
              // Dates kept as text inside jsonb (e.g. addresses metadata->>start_date) are mostly a
              // bare year ("2011"), sometimes YYYY-MM-DD. Compare them as text with no time part: from
              // the start year, up to (not including) the day after End.
              if (
                columnKey.includes('->>') &&
                (from === null || DATE_ONLY.test(String(from))) &&
                (to === null || DATE_ONLY.test(String(to)))
              ) {
                // With no start, '0' still keeps out blank values ('' sorts before every date).
                query = query.gte(columnKey, from !== null ? String(from).slice(0, 4) : '0');
                if (to !== null) {
                  // As text a bare year sorts before its own 1 January ('1998' < '1998-01-01'), so an
                  // End of 31 Dec stops at the next year itself, keeping that year out.
                  const after = nextDay(String(to));
                  query = query.lt(columnKey, after.endsWith('-01-01') ? after.slice(0, 4) : after);
                }
                break;
              }
              if (from !== null) query = query.gte(columnKey, dayStart(from));
              if (to !== null) {
                if (isRelative) {
                  // 'Older than N ...' keeps its strict boundary (unchanged behaviour)
                  query = query.lt(columnKey, dayStart(to));
                } else if (DATE_ONLY.test(String(to))) {
                  // Whole End day included: < UK midnight at the start of the day after End
                  query = query.lt(columnKey, dayStart(nextDay(String(to))));
                } else {
                  // Number ranges: To included
                  query = query.lte(columnKey, to);
                }
              }
              break;
            }
            case 'dual':
              // Handle range slider dual operator (min/max with gte/lte)
              const range = typeof value === 'string' ? JSON.parse(value) : value;
              if (range.min !== undefined && range.min !== null) {
                query = query.gte(columnKey, range.min);
              }
              if (range.max !== undefined && range.max !== null) {
                query = query.lte(columnKey, range.max);
              }
              break;
            default:
              query = query.eq(columnKey, value);
          }
        }
      }
    });

    return query;
  }

  /**
   * Apply sorting to a query.
   * @param {Object} query - The Supabase query object.
   * @param {string} by - The field to sort by.
   * @param {string} type - The sort type ('asc' or 'desc').
   * @param {Object} filters - The filters object (optional, used to determine nullsLast behavior).
   * @returns {Object} The modified query object.
   */
  static sorting(query, by, type, filters = {}) {
    // Ensure by is a string (fix for corrupted sortBy)
    if (typeof by !== 'string') {
      console.warn('sorting() received non-string by parameter:', by);
      return query;
    }
    if (by) {
      // Determine if nullsFirst should be false (nulls last)
      // Only use nulls last for:
      // 1. user_profiles table when in advanced mode (Pension Mode)
      // 2. pensions table (always)
      const shouldUseNullsLast =
        (this.table === 'user_profiles' && filters.isAdvancedMode) ||
        this.table === 'pensions';

      // Check if it's a JSON path (contains -> or ->>)
      if (by.includes('->')) {
        // JSON path sorting
        query = query.order(by, {
          ascending: type === 'asc',
          ...(shouldUseNullsLast && { nullsFirst: false })
        });
      } else {
        const [table, column] = by.split('.');
        if (column) {
          // Foreign key sorting
          const snakeColumn = column.replace(
            /[A-Z]/g,
            letter => `_${letter.toLowerCase()}`
          );
          query = query.order(`${table}(${snakeColumn})`, {
            ascending: type === 'asc',
            ...(shouldUseNullsLast && { nullsFirst: false })
          });
        } else {
          // Regular column sorting
          const snakeBy = by.replace(
            /[A-Z]/g,
            letter => `_${letter.toLowerCase()}`
          );
          query = query.order(snakeBy, {
            ascending: type === 'asc',
            ...(shouldUseNullsLast && { nullsFirst: false })
          });
        }
      }
    }
    return query;
  }

  /**
   * Apply pagination to a query.
   * @param {Object} query - The Supabase query object.
   * @param {number} page - The page number.
   * @param {number} size - The page size.
   * @returns {Object} The modified query object.
   */
  static range(query, page, size) {
    const from = (page - 1) * size;
    const to = from + size - 1;
    return query.range(from, to);
  }

  static async getCount(filters = {}) {
    let query = supabase
      .from(this.table)
      .select('id', { count: 'exact', head: true });

    query = this.filters(query, filters);

    const { count, error } = await query;

    if (error) throw error;

    return count;
  }

  /**
   * Get all records with filtering, sorting, and pagination.
   * @param {Object} filters - The filters to apply.
   * @param {number} page - The page number.
   * @param {number} size - The page size.
   * @param {string} sortBy - The field to sort by.
   * @param {string} sortType - The sort type ('asc' or 'desc').
   * @param {boolean} count - Whether to use core_counts table for count
   * @param {AbortSignal} abortSignal - An optional AbortSignal to abort the request.
   * @returns {Promise<Object>} A promise that resolves to an object containing the data and pagination info.
   */
  static async getAll(
    filters = {},
    page = 1,
    size = 10,
    sortBy = 'id',
    sortType = 'asc',
    count = true,
    abortSignal = null
  ) {
    try {
      let query = supabase
        .from(this.table)
        .select(this.selectQuery, { count: 'exact' });

      query = this.filters(query, filters);
      query = this.sorting(query, sortBy, sortType, filters);
      query = this.range(query, page, size);

      if (abortSignal) {
        query = query.abortSignal(abortSignal);
      }

      const { data, error, count: queryCount } = await query;

      if (error) throw error;

      return {
        data: data.map(item => new this(item)),
        count: queryCount,
        page: page,
        size: size,
        total: Math.ceil(queryCount / size)
      };
    } catch (error) {
      console.error(`[Error] Fetching All ${this.name}: `, error);
      throw error;
    }
  }
}

export default BaseModel;
